import { createReducer } from '@reduxjs/toolkit';
import {
  getAnonymousConfigurations,
  getContactInfo,
  getLanguagesInfo,
  getPublicKey,
  getPubliclyAvailableShifts,
  getPublicPharmacyRatingItems,
  getPublicProfessionalRatingItems,
  istermsAgreed,
  logout,
  professionalDeleteSignupDiploma,
  registerSmartLogin,
  resetLongTermContractAttributes,
  setBiometricEnabled,
  setBookedContractDates,
  setCampaignEmailTemplates,
  setCampaignImages,
  setCertificateFileName,
  setInitialLandingURL,
  setLoading,
  setLongTermContractAttributes,
  setNearbyProps,
  setProfessionalImage,
  setProfessionalResume,
  setSelectedIds,
  setSelectionMode,
  setShowBiometric,
  setAiOnboardingCallActive,
  setTechnicianDiploma,
  setTourCompleted,
  utilResetValue,
} from '../actions';
import {
  IAllLanguages,
  INearbyLocation,
  IPharmacyRatings,
  IProfessionalRatingModel,
  ILongContractAttributes,
} from '../models';
import { Agencies, defaultCenter, formUrl } from '../helpers/Constants';
import { IPublicAvailableShifts } from '../models/Models';
import { IFileAttributes, ILongTermUtils } from '../models/UtilModel';

export type CampaignImagesState = {
  [formUrl.activationEmailUrl]: null | string;
  [formUrl.bookingEmailUrl]: null | string;
  [formUrl.registrationEmailUrl]: null | string;
  [formUrl.registrationPopupUrl]: null | string;
};

export type CampaignEmailTemplates = {
  registrationEmailTemplatesubjectfr: string;
  registrationEmailTemplatebodyfr: string;
  registrationEmailImage: string;
  email: string;
  activationEmailTemplatesubjecten: string;
  activationEmailTemplatebodyen: string;
  activationEmailTemplatesubjectfr: string;
  activationEmailTemplatebodyfr: string;
  activationEmailImage: string;
  bookingEmailTemplatesubjecten: string;
  bookingEmailTemplatebodyen: string;
  bookingEmailTemplatesubjectfr: string;
  bookingEmailTemplatebodyfr: string;
  bookingEmailImage: string;
  registrationPopupImage: string;
  emailTemplateItem: string;
  multiBookingEmailTemplatesubjecten: string;
  multiBookingEmailTemplatebodyen: string;
  multiBookingEmailTemplatesubjectfr: string;
  multiBookingEmailTemplatebodyfr: string;
  existingUserEmailTemplatesubjectfr: string;
  existingUserEmailTemplatebodyfr: string;
};
export interface IUtils {
  termsAndConditions: boolean;
  publicAvailableShifts: IPublicAvailableShifts;
  contacts: any;
  languages: Array<IAllLanguages>;
  enableAIOnboarding: boolean;
  // Holds back the signup success redirect until the AI onboarding call has
  // spoken its closing message and ended.
  isAiOnboardingCallActive: boolean;
  loading: Array<string>;
  userHash: string | null;
  publicKey: string;
  isBiometricEnabled: boolean;
  isTourCompleted: boolean | null;
  showBiometric: boolean;
  nearbyLocation: INearbyLocation;
  selectedIds: Array<string>;
  selectionMode: {
    pharmacyId: string;
    showCheckbox: boolean;
    activeView?: string;
    isAutoSyncRecord?: boolean;
  };
  pharmacyRatingItems: IPharmacyRatings;
  professionalRatingItems: IProfessionalRatingModel;
  longTermContractAttributes: ILongTermUtils;
  bookedContractDates: Array<string>;
  initialLandingURL: string | null;
  certificateFileName: string;
  technicianDiploma: IFileAttributes;
  professionalResume: IFileAttributes;
  professionalImage: IFileAttributes;
  campaignImages: CampaignImagesState;
  campaignEmailTemplates: CampaignEmailTemplates;
}

const initialState: IUtils = {
  campaignEmailTemplates: {
    email: '',
    registrationEmailTemplatesubjectfr: '',
    registrationEmailTemplatebodyfr: '',
    registrationEmailImage: '',
    activationEmailTemplatesubjecten: '',
    activationEmailTemplatebodyen: '',
    activationEmailTemplatesubjectfr: '',
    activationEmailTemplatebodyfr: '',
    activationEmailImage: '',
    bookingEmailTemplatesubjecten: '',
    bookingEmailTemplatebodyen: '',
    bookingEmailTemplatesubjectfr: '',
    bookingEmailTemplatebodyfr: '',
    bookingEmailImage: '',
    registrationPopupImage: '',
    emailTemplateItem: 'registrationEmail',
    multiBookingEmailTemplatesubjecten: '',
    multiBookingEmailTemplatebodyen: '',
    multiBookingEmailTemplatesubjectfr: '',
    multiBookingEmailTemplatebodyfr: '',
    existingUserEmailTemplatesubjectfr: '',
    existingUserEmailTemplatebodyfr: '',
  },
  campaignImages: {
    [formUrl.activationEmailUrl]: null,
    [formUrl.bookingEmailUrl]: null,
    [formUrl.registrationEmailUrl]: null,
    [formUrl.registrationPopupUrl]: null,
  },
  professionalImage: {
    name: '',
    uri: '',
  },
  technicianDiploma: {
    name: '',
    uri: '',
  },
  professionalResume: {
    name: '',
    uri: '',
  },
  bookedContractDates: [],
  initialLandingURL: null,
  certificateFileName: '',
  longTermContractAttributes: {
    shiftContractAttributes: [],
    startDate: null,
    endDate: null,
    defaultEndTime: null,
    defaultStartTime: null,
  },
  professionalRatingItems: {
    displayDetailForm: false,
    professionalRatingItems: [],
    professionalRatingQuestions: [],
    favorite: false,
  },
  pharmacyRatingItems: {
    favorite: false,
    displayDetailForm: false,
    pharmacyRatingItems: [],
    pharmacyRatingQuestions: [],
  },
  publicAvailableShifts: {
    data: [],
    page: 1,
    itemPerPage: 1,
    totalCount: 0,
  },
  termsAndConditions: false,
  selectionMode: { pharmacyId: '', showCheckbox: false, activeView: '' },
  contacts: null,
  languages: [],
  enableAIOnboarding: false,
  isAiOnboardingCallActive: false,
  loading: [],
  userHash: null,
  publicKey: '',
  isBiometricEnabled: false,
  isTourCompleted: null,
  showBiometric: false,
  nearbyLocation: {
    latitude: defaultCenter.lat,
    longitude: defaultCenter.lng,
    showNearBy: false,
  },
  selectedIds: [],
};

const utilsReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(getContactInfo.fulfilled, (state, action) => {
      state.contacts = action.payload;
    })
    .addCase(setLoading, (state, action) => {
      state.loading = action.payload;
    })
    .addCase(getPublicKey.fulfilled, (state, action) => {
      state.publicKey = action.payload?.publicKey;
    })
    .addCase(registerSmartLogin.fulfilled, (state, action) => {
      state.userHash = action.payload?.userHash;
    })
    .addCase(setBiometricEnabled, (state, action) => {
      state.isBiometricEnabled = action.payload as boolean;
    })
    .addCase(setCertificateFileName, (state, action) => {
      state.certificateFileName = action.payload;
    })
    .addCase(setTourCompleted, (state, action) => {
      state.isTourCompleted = action.payload;
    })
    .addCase(logout.fulfilled, (state) => {
      state.publicKey = '';
      state.userHash = null;
    })
    .addCase(setShowBiometric, (state, action) => {
      state.showBiometric = action.payload;
    })
    .addCase(setAiOnboardingCallActive, (state, action) => {
      state.isAiOnboardingCallActive = action.payload;
    })
    .addCase(setNearbyProps, (state, action) => {
      state.nearbyLocation = action.payload;
    })
    .addCase(getLanguagesInfo.fulfilled, (state, action) => {
      let reverseLang = [];

      if (Array.isArray(action.payload)) {
        reverseLang = action.payload?.reverse?.();
      }

      state.languages = reverseLang;
    })
    .addCase(getAnonymousConfigurations.fulfilled, (state, action) => {
      state.enableAIOnboarding = !!action.payload?.enableAIOnboarding;
    })
    .addCase(getPubliclyAvailableShifts.fulfilled, (state, action) => {
      state.publicAvailableShifts = action.payload;
    })
    .addCase(setSelectionMode, (state, action) => {
      state.selectionMode = action.payload;
    })
    .addCase(istermsAgreed, (state, action) => {
      state.termsAndConditions = action.payload;
    })
    .addCase(getPublicPharmacyRatingItems.fulfilled, (state, action) => {
      state.pharmacyRatingItems = action.payload;
    })
    .addCase(getPublicProfessionalRatingItems.fulfilled, (state, action) => {
      state.professionalRatingItems = action.payload;
    })
    .addCase(setLongTermContractAttributes, (state, action) => {
      state.longTermContractAttributes = action.payload;
    })
    .addCase(setBookedContractDates, (state, action) => {
      state.bookedContractDates = action.payload;
    })
    .addCase(resetLongTermContractAttributes, (state) => {
      state.longTermContractAttributes =
        initialState.longTermContractAttributes;
    })
    .addCase(setInitialLandingURL, (state, action) => {
      state.initialLandingURL = action.payload;
    })
    .addCase(professionalDeleteSignupDiploma.fulfilled, (state, action) => {
      state.certificateFileName = '';
    })
    .addCase(setTechnicianDiploma, (state, action) => {
      state.technicianDiploma = action.payload;
    })
    .addCase(setProfessionalImage, (state, action) => {
      state.professionalImage = action.payload;
    })
    .addCase(setProfessionalResume, (state, action) => {
      state.professionalResume = action.payload;
    })
    .addCase(setCampaignImages, (state, action) => {
      state.campaignImages = action.payload;
    })
    .addCase(setCampaignEmailTemplates, (state, action) => {
      state.campaignEmailTemplates = action.payload;
    })
    .addCase(utilResetValue, (state, action) => {
      action.payload.map(
        (item) =>
          // @ts-expect-error: REDUCERS HAVE WRITABLE INNATE TYPE
          (state[item] = initialState[item as keyof typeof initialState]),
      );
    })
    .addCase(setSelectedIds, (state, action) => {
      state.selectedIds = action.payload;
    });
});

export default utilsReducer;
